import {Component, OnInit} from '@angular/core';
import {AuthService} from '@auth0/auth0-angular';
import {Router} from '@angular/router';
import {AuthOktaService} from '../../../shared/services/auth/auth-okta.service';
import {PrivilegeService} from '../../../shared/services/privilege.service';
import {AppConstant} from '../../../shared/utility/app-constant';
import {LoginLogoutService} from '../../../shared/services/auth/login-logout.service';
import {TimeOutService} from '../../../shared/services/auth/time-out.service';
import {firstValueFrom, filter} from 'rxjs';
import {AppEnumConstants} from "../../../shared/enums/app-enum-constants";
import {CommonUtility} from "../../../shared/utility/common-utility";

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss']
})
export class AuthCallbackComponent implements OnInit {

  public loading = true;
  public errorMessage = '';
  public mfaWindow;
  private commonUtility = new CommonUtility();

  constructor(private auth: AuthService, private router: Router, public authOktaService: AuthOktaService,
              public privilegeService: PrivilegeService, private loginService: LoginLogoutService,
              private timeOutService: TimeOutService) {
  }

  async ngOnInit() {
    this.loading = true;
    if (this.privilegeService.isAuth0Enabled()) {
      await this.mfaLogin();
      // await this.authorizeAndRedirect();
    } else {
      this.legacyLogin();
    }
  }

  async mfaLogin(): Promise<void> {
    try {
      const storedError = sessionStorage.getItem('auth_error');

      if (storedError) {
        const {error, errorDescription} = JSON.parse(storedError);
        sessionStorage.removeItem('auth_error');

        this.loading = false;

        if (error === 'access_denied' && errorDescription?.includes('verify')) {
          await this.router.navigate(['/verify-email'], {
            queryParams: {errorDescription: errorDescription || ''}
          });
          return;
        }

        if (error) {
          await this.router.navigate(['/access-denied'], {
            queryParams: {errorDescription: errorDescription || 'Unknown error'}
          });
          return;
        }
      }

      // Check if we are in a callback (have code and state)
      const params = new URLSearchParams(window.location.search);
      const isAuthenticated = await this.isAuthenticated();

      if (!isAuthenticated) {
        // Only redirect if NOT already on the callback page with code/state (to avoid tightness loop on failure)
        if (!params.has('code')) {
          await this.authOktaService.signIn();
        } else {
          if (!this.errorMessage) {
            this.errorMessage = 'Authentication failed. Please check your system time or try again.';
          }
          this.loading = false; // Stop loading spinner to show error
        }
      } else if (!params.has('code')){
        this.authorizeAndRedirect();
      }
    } catch (error) {
      this.commonUtility.logErrorToSentry(error, {
        tags: {
          errorType: AppEnumConstants.AUTH0,
          errorGroup: AppEnumConstants.AUTH
        },
        extra: {
          component: AppEnumConstants.AUTH0,
          method: 'mfaLogin',
          error
        }
      });
    }

  }

  legacyLogin() {
    const remember = localStorage.getItem(AppConstant.REMEMBER_ME);
    if (this.loginService.isUserLoggedIn() && JSON.parse(remember)) {
      this.privilegeService.navigateDashboard();
      this.loading = false;
    } else {
      this.router.navigateByUrl('login');
      this.loading = false;
    }
  }

  async isAuthenticated() {
    await firstValueFrom(this.auth.isLoading$.pipe(filter(isLoading => !isLoading)));
    const loggedIn = await firstValueFrom(this.auth.isAuthenticated$);
    if (loggedIn) {
      const user = await firstValueFrom(this.auth.user$);
    }
    return loggedIn;
  }

  async authorizeAndRedirect() {
    try {
      await this.authOktaService.getUserDetails();
      await this.timeOutService.getAuthenticatedUser(false, true);
      this.loading = false;
    } catch (error) {
      this.commonUtility.logErrorToSentry(error, {
        tags: {
          errorType: AppEnumConstants.AUTH0,
          errorGroup: AppEnumConstants.AUTH
        },
        extra: {
          component: AppEnumConstants.AUTH0,
          method: 'authorizeAndRedirect',
          error
        }
      });
      this.loading = false;
      // await this.router.navigate(['/access-denied'], {
      //   queryParams: { errorDescription: 'Failed to complete authentication' }
      // });
    }
  }

  authMfa() {
    const mfaData = {
      user_id: this.authOktaService.userDetail.sub,
      factor: 'otp',
      allow_multiple_enrollments: false
    };
    this.requestMfa(mfaData);
  }

  requestMfa(mfaData) {
    // 🔹 Step 1: open tab immediately (user gesture)
    this.mfaWindow = window.open('', '_blank');

    this.authOktaService.enableMfa(mfaData).subscribe({
      next: (res: any) => {
        // 🔹 Step 2: navigate the already-opened tab
        if (this.mfaWindow) {
          this.mfaWindow.location.href = res.ticket_url;
          this.listenForMfa();
        }
      },
      error: (err) => {
        // Close the empty tab if something went wrong
        if (this.mfaWindow && !this.mfaWindow.closed) {
          this.mfaWindow.close();
          this.mfaWindow = null;
        }
      }
    });
  }

  listenForMfa() {
    this.authOktaService.listenForMfa(this.authOktaService.userDetail.sub).subscribe({
      next: (data) => {
        console.log('✅ MFA completed:', data);

        if (this.mfaWindow) {
          // Give user a few seconds, then close
          setTimeout(() => {
            if (this.mfaWindow && !this.mfaWindow.closed) {
              this.mfaWindow.close();
              this.mfaWindow = null;
            }
          }, 5000);
        }
      },
      error: (err) => console.error('SSE error', err)
    });
  }


  login() {
    this.authOktaService.getEnrollments();
  }

  webAuthEnroll() {
    const mfaData = {
      user_id: this.authOktaService.userDetail.sub,
      factor: 'webauthn-platform',
      allow_multiple_enrollments: true
    };
    this.requestMfa(mfaData);
  }

  addOrg() {
    const metaData = {
      orgId: 'org_XbZ4l3Trga5ddwcp',
      userIds: [
        'auth0|690858fcdf61199f938252ec',
        'auth0|690435e9f16ed4d9a8a94f74',
      ]
    };
    this.authOktaService.addUsersOrg(metaData).subscribe(value => {
      console.log(value);
    });
  }

  updateOrg() {
    const metaData = {
      orgId: 'org_XbZ4l3Trga5ddwcp',
      metadata: {
        mfa_required: 'true',
        tenantId: 'qa-app356-portal'
      }
    };
    this.authOktaService.updateOrg(metaData).subscribe(value => {
      console.log(value);
    });
  }
}
